import React from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { FloatingInput, Button, ClosedSelect } from '@piplapps/pipl-ui';
import { getText } from '../../../Utils/TranslationUtils';
import styles from './LeadVettingFormStyles.module.less';
import { trackEvent } from '../utils';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler/AnalyticsHandler';
import PageviewHandler from '../../../Utils/AnalyticsHandler/PageviewHandler';

const VettingStep2 = (props) => {
  const { isLoading, product } = props;
  const productIsSearch = product?.includes('SEARCH');

  const validate = (values) => {
    const errors = {};
    const notRequiredFields = ['teamSize', 'otherIndustryDescription'];
    for (const value in values) {
      if (!notRequiredFields.includes(value)) {
        if (values[value].trim() === '') {
          errors[value] = getText('ERROR_REQUIRED_FIELD');
        }
      }
    }
    if (
      values.industry === 'other' &&
      (values.otherIndustryDescription.trim() === '' || !values.otherIndustryDescription)
    ) {
      errors.otherIndustryDescription = getText('ERROR_REQUIRED_FIELD');
    }
    if (Object.values(errors).length > 0) {
      props.removeCompleted(2);
    }

    if (values.teamSize.trim() === '' && productIsSearch) {
      errors.teamSize = getText('ERROR_REQUIRED_FIELD');
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      company: props.fields?.company ? props.fields.company : '',
      job: props.fields?.job ? props.fields.job : '',
      teamSize: props.fields?.teamSize ? props.fields.teamSize : '',
      industry: props.fields?.industry ? props.fields.industry : '',
      otherIndustryDescription: props.fields?.otherIndustryDescription ? props.fields?.otherIndustryDescription : '',
    },
    validate,
    onSubmit: (values) => {
      trackEvent('Step1', '');
      props.completeStep(2);
      props.onSubmit(values);
    },
  });

  const teamSizeOptions = [
    { value: '1-5', label: '1 to 5 Team members' },
    { value: '5-10', label: '5 to 10 Team members' },
    { value: '10-15', label: '10 to 15 Team members' },
    { value: '15-more', label: 'More than 15 Team members' },
  ];
  const industryOptions = [
    { value: 'e_commerce', label: 'E-Commerce & Retail' },
    { value: 'banking', label: 'Financial Services & Banking' },
    { value: 'government', label: 'Government & Law Enforcement' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'it', label: 'IT & Cybersecurity' },
    { value: 'legal', label: 'Legal Services' },
    { value: 'media', label: 'Media & Journalism' },
    { value: 'private_investigations', label: 'Private Investigations, Skip Tracing, & Collections' },
    { value: 'recruiting', label: 'Recruiting & HR' },
    { value: 'sales_and_marketing', label: 'Sales & Marketing' },
    { value: 'travel', label: 'Ticket, Travel, & Hospitality' },
    { value: 'other', label: 'Other' },
  ];

  const onBlur = (e, fieldName) => {
    const fieldObj = { [fieldName]: formik.values[fieldName] };
    formik.handleBlur(e);
    trackEvent(fieldName, e.target.value);
    props.updateFields(fieldObj);
  };

  return (
    <>
      <PageviewHandler path="step2/" />
      <div className={styles.form}>
        <div className={styles.headerWrapper}>
          <div className={styles.pageTitle}>
            <span>{gettext('Business Application')}</span>
          </div>
        </div>
        <div>
          <form onSubmit={formik.handleSubmit} id="vetting-step-2-form">
            <div className={styles.fieldRow}>
              <FloatingInput
                id="id_company"
                name="company"
                placeholder={gettext('Company Name')}
                testId="company-input"
                onChange={formik.handleChange}
                onBlur={(e) => onBlur(e, 'company')}
                value={formik.values.company ? formik.values.company : ''}
                forcedError={formik.errors.company && formik.touched.company ? formik.errors.company : null}
                error={formik.errors.company}
              />
            </div>
            <div className={styles.fieldRow}>
              <FloatingInput
                id="id_job"
                name="job"
                placeholder={gettext('Job Title')}
                testId="job-title-input"
                onChange={formik.handleChange}
                onBlur={(e) => onBlur(e, 'job')}
                value={formik.values.job ? formik.values.job : ''}
                forcedError={formik.errors.job && formik.touched.job ? formik.errors.job : null}
                error={formik.errors.job}
              />
            </div>
            {productIsSearch ? (
              <div className={styles.fieldRow}>
                <ClosedSelect
                  id="id_teamSize"
                  name="teamSize"
                  label={gettext('Team Size')}
                  testId="team-size-input"
                  options={teamSizeOptions}
                  setValue={(v) => v}
                  height={45}
                  onChange={(op) => formik.setFieldValue('teamSize', op)}
                  value={formik.values.teamSize ? formik.values.teamSize : ''}
                  error={formik.errors.teamSize && formik.touched.teamSize ? formik.errors.teamSize : null}
                  onBlur={() => props.updateFields({ teamSize: formik.values.teamSize })}
                />
              </div>
            ) : null}
            <div className={styles.fieldRow}>
              <ClosedSelect
                id="id_industry"
                name="industry"
                label={gettext('Industry')}
                testId="industry-input"
                options={industryOptions}
                setValue={(v) => v}
                height={45}
                onChange={(v) => formik.setFieldValue('industry', v)}
                value={formik.values.industry ? formik.values.industry : ''}
                error={formik.errors.industry && formik.touched.industry ? formik.errors.industry : null}
                onBlur={() => props.updateFields({ industry: formik.values.industry })}
              />
            </div>
            {formik.values.industry === 'other' ? (
              <div className={styles.wideInput}>
                <FloatingInput
                  size="wide"
                  placeholder={gettext('Specify your industry')}
                  id="otherIndustryDescription_id"
                  name="otherIndustryDescription"
                  testId="otherIndustryDescription-input"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.otherIndustryDescription ? formik.values.otherIndustryDescription : ''}
                  forcedError={
                    formik.errors.otherIndustryDescription &&
                    formik.touched.otherIndustryDescription &&
                    formik.values.industry === 'other'
                      ? formik.errors.otherIndustryDescription
                      : null
                  }
                  error={formik.errors.otherIndustryDescription}
                  disabled={formik.values.industry !== 'other'}
                  height={100}
                  autoFocused
                />
              </div>
            ) : null}
            <div className={styles.proceedButton}>
              <AnalyticsHandler eventName={AnalyticsEventNames.SUBMIT_STEP_2}>
                <Button
                  id="submit-button"
                  buttonClass="primary-green"
                  type="submit"
                  text={gettext('Proceed')}
                  isLoading={isLoading}
                  loadingText={gettext('Saving')}
                />
              </AnalyticsHandler>
            </div>
          </form>
        </div>
        <div className={styles.buffer}>
          <span />
        </div>
      </div>
    </>
  );
};
VettingStep2.propTypes = {
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  fields: PropTypes.object,
  completeStep: PropTypes.func,
  removeCompleted: PropTypes.func,
  updateFields: PropTypes.func,
  product: PropTypes.string,
};

export default VettingStep2;
